/**
 * THE CURRENT CONNECT PROGRAM — the one "Register now" means.
 *
 * Connect's database only holds Connect's own programs (host portal →
 * Programs), so no form_key filter is needed. The current program is the
 * LATEST one still running: active, open for registration (enrollable), not
 * yet finished — the one with the latest start date wins. A season program
 * ("Connect 2026/2027") and any extra activity added later can therefore
 * coexist; a bare "Register now" always lands on the newest.
 *
 * `workers/connect-api` applies the SAME rule server-side when a registration
 * arrives without a program id — keep the two in step.
 */
import { useQuery } from "@tanstack/react-query";
import {
  apiClient,
  canEnrolNow,
  hasProgramFinished,
  todayInSydney,
  type Program,
} from "@alight/contracts";

/** Latest start date first (undated last), then sort_order, then newest id. */
function compareCurrent(a: Program, b: Program): number {
  const sa = a.start_date ?? "";
  const sb = b.start_date ?? "";
  if (sa !== sb) {
    if (!sa) return 1;
    if (!sb) return -1;
    return sa < sb ? 1 : -1;
  }
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return b.id - a.id;
}

export function pickCurrentProgram(programs: Program[], today: string = todayInSydney()): Program | null {
  const candidates = programs.filter(
    (p) =>
      p.active === 1 &&
      p.enrollable === 1 &&
      (p as { listed?: number }).listed !== 0 &&
      !hasProgramFinished(p, today),
  );
  return [...candidates].sort(compareCurrent)[0] ?? null;
}

/** Can someone register for this program today (cut-off, end date, flags)? */
export function isRegistrationOpen(program: Program | null, today: string = todayInSydney()): boolean {
  return program !== null && canEnrolNow(program, today);
}

const api = apiClient("");

/** Public programs + the current one. Shares the ["programs"] cache key with
 *  every other page that lists programs. */
export function useCurrentProgram() {
  const query = useQuery({
    queryKey: ["programs"],
    queryFn: () => api.getPrograms(),
    retry: false,
  });
  const programs = query.data ?? [];
  const program = pickCurrentProgram(programs);
  return {
    programs,
    program,
    open: isRegistrationOpen(program),
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
